import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function NewEstimate() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [priceBook, setPriceBook] = useState([]);
  const [form, setForm] = useState({ customer_id: "", title: "", notes: "" });
  const [items, setItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, p] = await Promise.all([api.get("/customers"), api.get("/price-book")]);
      setCustomers(c.data);
      setPriceBook(p.data);
    })();
  }, []);

  const total = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0), [items]);

  const addService = (svc) => {
    setItems((arr) => [...arr, { description: svc.name, quantity: 1, unit_price: svc.price }]);
    setPickerOpen(false);
    setSearch("");
  };

  const addCustom = () => setItems((arr) => [...arr, { description: "", quantity: 1, unit_price: 0 }]);
  const updateItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: k === "description" ? v : Number(v) } : it));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const filteredServices = priceBook.filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.category.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    if (!form.customer_id || !form.title.trim()) {
      toast.error("Customer and title are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/estimates", { ...form, items });
      toast.success("Estimate created");
      nav(`/estimates/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <Link to="/estimates" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-semibold">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Link>
      <h1 className="text-3xl font-bold">New Estimate</h1>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4">
        <div>
          <Label className="text-sm font-semibold">Customer *</Label>
          <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="est-customer"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold">Title *</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12 mt-1.5" placeholder="e.g. Water heater replacement" data-testid="est-title" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Customer Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[80px] mt-1.5" data-testid="est-notes" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Line Items</h2>
          <div className="flex gap-2">
            <Button onClick={() => setPickerOpen(true)} className="h-10 px-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm" data-testid="est-add-from-pricebook">
              <BookOpen className="w-4 h-4 mr-1" /> From Price Book
            </Button>
            <Button onClick={addCustom} variant="outline" className="h-10 px-3 rounded-xl" data-testid="est-add-custom"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="space-y-2">
          {items.length === 0 && <p className="text-slate-500 text-sm">Add line items from the Price Book or create custom ones.</p>}
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`est-line-${idx}`}>
              <Input className="col-span-12 sm:col-span-6 h-11" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description" />
              <Input className="col-span-4 sm:col-span-2 h-11" type="number" step="0.5" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
              <Input className="col-span-6 sm:col-span-3 h-11" type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} />
              <button onClick={() => removeItem(idx)} className="col-span-2 sm:col-span-1 h-11 rounded-xl text-red-600 hover:bg-red-50 flex items-center justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
          <div className="text-slate-500 font-semibold">Estimated Total</div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="est-total">${total.toFixed(2)}</div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="est-save">
        {saving ? "Saving…" : "Save Estimate"}
      </Button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Add from Price Book</DialogTitle></DialogHeader>
          <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-11" />
          <div className="max-h-[60vh] overflow-y-auto space-y-1">
            {filteredServices.map((s) => (
              <button
                key={s.id}
                onClick={() => addService(s)}
                className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition"
                data-testid={`pick-service-${s.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 font-semibold">{s.category}</div>
                    <div className="font-semibold truncate">{s.name}</div>
                  </div>
                  <div className="font-bold text-blue-600">${Number(s.price).toFixed(2)}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
